/**
 * @packageDocumentation
 *
 * `@helia/car` provides `import` and `export` methods to read/write Car files
 * to {@link https://github.com/ipfs/helia Helia}'s blockstore.
 *
 * See the {@link Car} interface for all available operations.
 *
 * By default it supports `dag-pb`, `dag-cbor`, `dag-json` and `raw` CIDs, more
 * esoteric DAG walkers can be passed as an init option.
 *
 * @example Exporting a DAG as a CAR file
 *
 * ```typescript
 * import { createHelia } from 'helia'
 * import { car } from '@helia/car'
 * import { CID } from 'multiformats/cid'
 * import nodeFs from 'node:fs'
 *
 * const helia = await createHelia()
 * const cid = CID.parse('QmFoo...')
 *
 * const c = car(helia)
 * const out = nodeFs.createWriteStream('example.car')
 *
 * for await (const buf of c.stream(cid)) {
 *   out.write(buf)
 * }
 *
 * out.end()
 * ```
 *
 * @example Exporting a part of a UnixFS DAG as a CAR file
 *
 * ```typescript
 * import { createHelia } from 'helia'
 * import { car, UnixFSPath } from '@helia/car'
 * import { CID } from 'multiformats/cid'
 * import nodeFs from 'node:fs'
 *
 * const helia = await createHelia()
 * const cid = CID.parse('QmFoo...')
 *
 * const c = car(helia)
 * const out = nodeFs.createWriteStream('example.car')
 *
 * for await (const buf of c.stream(cid, {
 *   traversal: new UnixFSPath('/foo/bar/baz.txt')
 * })) {
 *   out.write(buf)
 * }
 *
 * out.end()
 * ```
 *
 * @example Importing all blocks from a CAR file
 *
 * ```typescript
 * import { createHelia } from 'helia'
 * import { unixfs } from '@helia/unixfs'
 * import { car } from '@helia/car'
 * import { CarReader } from '@ipld/car'
 * import { Readable } from 'node:stream'
 * import nodeFs from 'node:fs'
 *
 * const helia = await createHelia({
 *   // ... helia config
 * })
 *
 * // import the car
 * const inStream = nodeFs.createReadStream('example.car')
 * const reader = await CarReader.fromIterable(inStream)
 *
 * const c = car(helia)
 * await c.import(reader)
 * ```
 */
import { Car as CarClass } from './car.js';
export * from './export-strategies/index.js';
export * from './traversal-strategies/index.js';
/**
 * Create a {@link Car} instance for use with {@link https://github.com/ipfs/helia Helia}
 */
export function car(helia, init = {}) {
    return new CarClass(helia, init);
}
//# sourceMappingURL=index.js.map